import React, { useState, useEffect, useRef } from 'react';
import { X, Copy, Check, Mail, Shield, Trash2, ChevronRight, Brain, Send, MessageCircle, ArrowLeft, Star, Coffee, RefreshCw } from 'lucide-react';
import './SettingsModal.css';
import VoiceModeModal from './VoiceModeModal';
import { SecureAPI } from '../config.js';

const SettingsModal = ({ 
  isOpen, 
  onClose, 
  preptuneId, 
  userName,
  onNicknameUpdate,
  firestoreService,
  AZURE_OPENAI_ENDPOINT,
  AZURE_OPENAI_API_KEY,
  AZURE_OPENAI_DEPLOYMENT,
  selectedVoiceHost,           // ✅ ADD THIS
  onVoiceUpdate,               // ✅ ADD THIS
}) => {
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  
  // Profile Tab State
  const [deletingMemoryIds, setDeletingMemoryIds] = useState(new Set()); // ✅ Track multiple deletions
  const [nickname, setNickname] = useState(userName || '');
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [isSavingNickname, setIsSavingNickname] = useState(false);
  const [nicknameError, setNicknameError] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [showRevealId, setShowRevealId] = useState(false);
  const [showIdCopyFeedback, setShowIdCopyFeedback] = useState(false);
  const [showEmailCopyFeedback, setShowEmailCopyFeedback] = useState(false);
const [selectedVoice, setSelectedVoice] = useState(null);
const [showVoiceChangeModal, setShowVoiceChangeModal] = useState(false);
const [deletingMemoryId, setDeletingMemoryId] = useState(null);
const [isClearingAll, setIsClearingAll] = useState(false);
const [editingMemoryId, setEditingMemoryId] = useState(null);
const [editingMemoryText, setEditingMemoryText] = useState('');
const [isSavingMemory, setIsSavingMemory] = useState(false);
const [showVoiceDropdown, setShowVoiceDropdown] = useState(false);
const [isPlayingPreview, setIsPlayingPreview] = useState(false);
const voicePreviewRef = useRef(null);
  // Memories Tab State
  const [memories, setMemories] = useState([]);
  const [structuredMemories, setStructuredMemories] = useState({
    'Your Goals': [],
    'Your Preferences': [],
    'Your Habits': []
  });
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [chatMode, setChatMode] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [memoryToDelete, setMemoryToDelete] = useState(null);
  const [showDeleteMemoryConfirm, setShowDeleteMemoryConfirm] = useState(false);

  // AI Cooldown State
  const [lastAIStructureTime, setLastAIStructureTime] = useState(0);
  const [canRefreshAI, setCanRefreshAI] = useState(true);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const COOLDOWN_DURATION = 300000; // 5 minutes cooldown (300,000ms)
  const hasStructuredRef = useRef(false);
  const memoryCacheRef = useRef(null);
  const cacheHashRef = useRef(null);
const [playingVoiceId, setPlayingVoiceId] = useState(null);

  // UI State
  const [activeTab, setActiveTab] = useState('profile');
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  useEffect(() => {
  if (isOpen && firestoreService) {
    loadEmail();
    loadMemories();
    loadSelectedVoice(); // ✅ ADD THIS
  }
}, [isOpen, firestoreService]);
  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    if (isOpen && firestoreService) {
      loadEmail();
      loadMemories();
    }
  }, [isOpen, firestoreService]);

  // Cooldown timer
  useEffect(() => {
    if (!canRefreshAI && cooldownRemaining > 0) {
      const timer = setInterval(() => {
        setCooldownRemaining(prev => {
          const newValue = prev - 1000;
          if (newValue <= 0) {
            setCanRefreshAI(true);
            return 0;
          }
          return newValue;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [canRefreshAI, cooldownRemaining]);

  // Only structure once when memories load initially
  useEffect(() => {
    if (memories.length > 0 && activeTab === 'memories' && !chatMode && !hasStructuredRef.current) {
      structureMemories();
      hasStructuredRef.current = true;
    }
  }, [memories, activeTab, chatMode]);

  // Reset structured flag when tab changes or modal closes
  useEffect(() => {
    if (!isOpen || activeTab !== 'memories') {
      hasStructuredRef.current = false;
    }
  }, [isOpen, activeTab]);

  
const loadSelectedVoice = async () => {
  try {
    if (firestoreService?.isReady()) {
      const userData = await firestoreService.getUserData();
      const savedVoice = userData?.settings?.selectedVoice || 'Host 1';
      setSelectedVoice(savedVoice);
    }
  } catch (error) {
    console.error('Failed to load voice:', error);
    setSelectedVoice('Host 1');
  }
};
const handleEditMemoryClick = (memoryText, memoryId) => {
  setEditingMemoryId(memoryId);
  setEditingMemoryText(memoryText);
};

const handleCancelEdit = () => {
  setEditingMemoryId(null);
  setEditingMemoryText('');
};
const handleVoiceSelect = async (voiceId) => {
  try {
    console.log('💾 Selecting voice:', voiceId);
    
    if (firestoreService?.isReady()) {
      await firestoreService.updateUserData({
        'settings.selectedVoice': voiceId
      });
      
      setSelectedVoice(voiceId);
      setShowVoiceDropdown(false);
      
      if (onVoiceUpdate) {
        onVoiceUpdate(voiceId);
      }
      
      console.log('✅ Voice saved to Firestore');
    }
  } catch (error) {
    console.error('❌ Failed to save voice:', error);
  }
};

const handlePlayVoicePreview = async (voiceId) => {
  const voiceFiles = {
    'Host 1': '/sound/voice1.wav',
    'Host 2': '/sound/bob-voice.wav',
    'Host 3': '/sound/jackie-voice.wav',
    'Host 4': '/sound/alex-voice.wav'
  };
  
  const audioFile = voiceFiles[voiceId];
  
  if (!audioFile) {
    console.warn('No audio file for voice:', voiceId);
    return;
  }

  // If clicking the SAME voice that's currently playing, stop it
  if (isPlayingPreview && playingVoiceId === voiceId && voicePreviewRef.current) {
    voicePreviewRef.current.pause();
    voicePreviewRef.current.currentTime = 0;
    setIsPlayingPreview(false);
    setPlayingVoiceId(null);
    return;
  }

  // If clicking a DIFFERENT voice, play the new one
  try {
    // Stop any currently playing audio
    if (voicePreviewRef.current) {
      voicePreviewRef.current.pause();
      voicePreviewRef.current.currentTime = 0;
    }

    if (!voicePreviewRef.current) {
      voicePreviewRef.current = new Audio();
    }

    voicePreviewRef.current.src = audioFile;
    voicePreviewRef.current.onended = () => {
      setIsPlayingPreview(false);
      setPlayingVoiceId(null);
    };
    
    await voicePreviewRef.current.play();
    setIsPlayingPreview(true);
    setPlayingVoiceId(voiceId);
  } catch (error) {
    console.error('Error playing preview:', error);
    setIsPlayingPreview(false);
    setPlayingVoiceId(null);
  }
};
const handleSaveEdit = async () => {
  if (!editingMemoryText.trim() || !editingMemoryId) {
    return;
  }

  try {
    setIsSavingMemory(true);
    
    if (!firestoreService?.memoryService) {
      throw new Error('Memory service not available');
    }

    console.log('📝 Saving edited memory:', editingMemoryId);

    // ✅ STEP 1: Update in Firestore
    await firestoreService.memoryService.updateMemory(editingMemoryId, {
      memory: editingMemoryText.trim()
    });

    console.log('✅ Firestore updated');

    // ✅ STEP 2: Update local state IMMEDIATELY (visual feedback)
    setMemories(prev => 
      prev.map(m => 
        m.id === editingMemoryId 
          ? { ...m, memory: editingMemoryText.trim() }
          : m
      )
    );

    // ✅ STEP 3: Clear cache to force re-structure
    memoryCacheRef.current = null;
    cacheHashRef.current = null;
    hasStructuredRef.current = false;

    // ✅ STEP 4: Update structured memories in real-time ⭐ THIS WAS MISSING!
    setStructuredMemories(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(category => {
        updated[category] = updated[category].map(item => {
          if (item.id === editingMemoryId) {
            return {
              ...item,
              text: editingMemoryText.trim(),
              originalText: editingMemoryText.trim()
            };
          }
          return item;
        });
      });
      return updated;
    });

    // ✅ STEP 5: Reset edit state
    setEditingMemoryId(null);
    setEditingMemoryText('');
    
    console.log('✅ Memory updated successfully (local + Firestore)');
    
  } catch (error) {
    console.error('❌ Failed to update memory:', error);
    alert('Failed to update memory. Please try again.');
    
    // ✅ Rollback if needed
    setEditingMemoryId(null);
    setEditingMemoryText('');
  } finally {
    setIsSavingMemory(false);
  }
};
// ============================================
// ADD THIS FUNCTION TO SAVE VOICE
// ============================================

const handleVoiceUpdate = async (newVoice) => {
  try {
    console.log('💾 Saving voice selection:', newVoice);
    
    if (firestoreService?.isReady()) {
      await firestoreService.updateUserData({
        'settings.selectedVoice': newVoice
      });
      
      setSelectedVoice(newVoice);
      setShowVoiceChangeModal(false);
      
      if (onVoiceUpdate) {
        onVoiceUpdate(newVoice);
      }
      
      console.log('✅ Voice saved to Firestore');
    }
  } catch (error) {
    console.error('❌ Failed to save voice:', error);
  }
};

  // ============================================
  // DATA LOADING
  // ============================================

  const loadEmail = async () => {
    try {
      if (firestoreService?.isReady()) {
        const userData = await firestoreService.getUserData();
        setUserEmail(userData.email || 'No email found');
      }
    } catch (error) {
      console.error('Failed to load email:', error);
      setUserEmail('Error loading email');
    }
  };

// ============================================
// IMPROVED: loadMemories() - Tries Multiple Methods
// ============================================

const loadMemories = async () => {
  try {
    if (!firestoreService?.memoryService) {
      console.warn('⚠️ Memory service not available');
      setMemories([]);
      return;
    }

    console.log('📂 Loading memories from Firestore...');
    
    let allMemories = [];
    let loadMethod = 'unknown';

    // ✅ METHOD 1: Try getAllMemories first
    console.log('   Trying getAllMemories()...');
    try {
      const result = await firestoreService.memoryService.getAllMemories?.();
      if (result && Array.isArray(result) && result.length > 0) {
        allMemories = result;
        loadMethod = 'getAllMemories';
        console.log(`   ✅ Loaded ${result.length} memories via getAllMemories`);
      }
    } catch (e) {
      console.log(`   ⚠️ getAllMemories failed: ${e.message}`);
    }

    // ✅ METHOD 2: If getAllMemories failed or returned few, try getRecentMemories
    if (allMemories.length < 5) {
      console.log('   Trying getRecentMemories(50, 365)...');
      try {
        const result = await firestoreService.memoryService.getRecentMemories?.(50, 365);
        if (result && Array.isArray(result) && result.length > allMemories.length) {
          allMemories = result;
          loadMethod = 'getRecentMemories';
          console.log(`   ✅ Loaded ${result.length} memories via getRecentMemories`);
        }
      } catch (e) {
        console.log(`   ⚠️ getRecentMemories failed: ${e.message}`);
      }
    }

    // ✅ METHOD 3: Try combining short + long term
    if (allMemories.length < 5) {
      console.log('   Trying combined getShortTermMemories + getLongTermMemories...');
      try {
        const shortTerm = await firestoreService.memoryService.getShortTermMemories?.(30) || [];
        const longTerm = await firestoreService.memoryService.getLongTermMemories?.(30) || [];
        
        // Combine and deduplicate by ID
        const combined = [...shortTerm, ...longTerm];
        const uniqueIds = new Set();
        allMemories = combined.filter(m => {
          if (uniqueIds.has(m.id)) return false;
          uniqueIds.add(m.id);
          return true;
        });
        
        if (allMemories.length > 0) {
          loadMethod = 'combined short + long term';
          console.log(`   ✅ Loaded ${allMemories.length} memories via combined method`);
        }
      } catch (e) {
        console.log(`   ⚠️ Combined method failed: ${e.message}`);
      }
    }

    // ✅ METHOD 4: Direct Firestore query if all else fails
    if (allMemories.length < 5 && firestoreService?.db) {
      console.log('   Trying direct Firestore query...');
      try {
        const { collection, query, getDocs, orderBy, limit } = await import('firebase/firestore');
        
        const memoriesRef = collection(firestoreService.db, 'users', firestoreService.user?.uid, 'memories');
        const q = query(memoriesRef, orderBy('savedAt', 'desc'), limit(50));
        const snapshot = await getDocs(q);
        
        allMemories = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        loadMethod = 'direct Firestore';
        console.log(`   ✅ Loaded ${allMemories.length} memories via direct Firestore`);
      } catch (e) {
        console.log(`   ⚠️ Direct Firestore failed: ${e.message}`);
      }
    }

    // ✅ Sort by date (newest first) to get most relevant memories
    const sortedMemories = allMemories
      .filter(m => m.id && m.memory) // Ensure valid memories
      .sort((a, b) => {
        const dateA = a.savedAt?.toDate?.() || new Date(a.savedAt) || new Date(0);
        const dateB = b.savedAt?.toDate?.() || new Date(b.savedAt) || new Date(0);
        return dateB - dateA; // Newest first
      });
    
    // ✅ Validate that memories have IDs
    const validMemories = sortedMemories.filter(m => {
      if (!m.id) {
        console.warn('⚠️ Memory missing ID:', m);
        return false;
      }
      return true;
    });
    
    console.log(`✅ Loaded ${validMemories.length} memories with IDs (sorted by date)`);
    console.log(`   Method used: ${loadMethod}`);
    
    // ✅ Log memory details
    const counts = {
      total: validMemories.length,
      withDate: validMemories.filter(m => m.savedAt).length,
      withTopic: validMemories.filter(m => m.topic).length,
      withImportance: validMemories.filter(m => m.importance).length
    };
    console.log('📊 Memory stats:', counts);
    
    // ✅ Log first few memories
    if (validMemories.length > 0) {
      console.log('📝 First 3 memories:');
      validMemories.slice(0, 3).forEach((m, i) => {
        const date = m.savedAt 
          ? new Date(m.savedAt.toDate?.() || m.savedAt).toLocaleDateString()
          : 'unknown';
        console.log(`   ${i + 1}. [${date}] ${m.memory.substring(0, 50)}...`);
      });
    }
    
    setMemories(validMemories);
  } catch (error) {
    console.error('❌ Failed to load memories:', error);
    setMemories([]);
  }
};


// ============================================
// FIXED: structureMemories() - Use 30+ memories for better context
// ============================================

const structureMemories = async (forceRefresh = false) => {
  try {
    if (!memories.length) {
      console.warn('⚠️ No memories to structure');
      return;
    }

    const currentHash = getMemoryHash(memories);

    if (!forceRefresh && memoryCacheRef.current && cacheHashRef.current === currentHash) {
      console.log('📦 Using cached memory structure');
      setStructuredMemories(memoryCacheRef.current);
      return;
    }

    const now = Date.now();
    const timeSinceLastStructure = now - lastAIStructureTime;
    
    if (!forceRefresh && timeSinceLastStructure < COOLDOWN_DURATION && lastAIStructureTime > 0) {
      console.log('⏱️ AI structuring on cooldown. Using cached data if available.');
      if (memoryCacheRef.current) {
        setStructuredMemories(memoryCacheRef.current);
      }
      return;
    }

    setIsLoadingProfile(true);
    setLastAIStructureTime(now);
    setCanRefreshAI(false);
    setCooldownRemaining(COOLDOWN_DURATION);

    // ✅ INCREASED: Use top 30 memories instead of 15 for better coverage
    const topMemories = memories.slice(0, 30);
    console.log(`🔍 Using top ${topMemories.length} memories for structuring (out of ${memories.length} total)`);
    
    // ✅ NEW: Create indexed memory list with more context
    const memoryListWithIndices = topMemories.map((m, i) => {
      const dateStr = m.savedAt 
        ? new Date(m.savedAt.toDate?.() || m.savedAt).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
          })
        : 'date unknown';
      
      return `[${i}] (${dateStr}) ${m.memory}`;
    }).join('\n');

    console.log('📝 Memory list for AI:\n', memoryListWithIndices.substring(0, 200) + '...');

const response = await SecureAPI.callAzureOpenAI([
  {
    role: 'system',
    content: `You're organizing memories about a real person. Make it PERSONAL, CONVERSATIONAL, and DEEPLY RESPECTFUL.

CRITICAL RULES:
1. Use their EXACT words and specific details - don't generalize
2. Write like you're talking TO them, not ABOUT them
3. Keep their personality and enthusiasm
4. Use first-person perspective naturally ("You love French" not "Wants to learn French")
5. ALWAYS be respectful - never judgmental, dismissive, or casual about serious topics
6. Honor their privacy - keep sensitive information dignified
7. Celebrate their achievements and efforts without being condescending

Organize into these categories (use exact keys):
- "Things You're Working On" - goals, learning, projects they mentioned
- "What You Love" - interests, passions, hobbies, what excites them
- "How You Roll" - routines, habits, preferences, lifestyle

TONE EXAMPLES:
❌ "Wants to learn French" → ✅ "You're learning French and setting study reminders to stay on track"
❌ "Prefers coffee" → ✅ "You're a coffee person over tea"
❌ "Studying computer science" → ✅ "You're a CS student working through algorithms"

ORGANIZATION RULES:
- **Show EXACTLY 2-3 memories per category** (pick the most interesting/recent ones)
- If a category has fewer than 2 memories, show what exists
- If a category has many, pick the most recent and most unique ones
- Skip categories with no relevant memories
- Keep it SHORT - one clear sentence per memory (max 15 words)
- Sound natural and warm, not clinical or robotic

✅ CRITICAL: Return BOTH the reworded text AND the index number from the original list!

Return format:
{
  "Things You're Working On": [
    {"index": 0, "text": "You're learning French with daily study reminders"},
    {"index": 5, "text": "You're working on your CS project"},
    {"index": 12, "text": "You're training for a marathon"}
  ],
  "What You Love": [
    {"index": 2, "text": "You're a coffee person over tea"},
    {"index": 7, "text": "You love hiking on weekends"},
    {"index": 15, "text": "You're passionate about photography"}
  ],
  "How You Roll": [
    {"index": 3, "text": "You wake up at 6am every day"},
    {"index": 8, "text": "You meditate for 10 minutes before work"}
  ]
}

Indexed memories to organize:
${memoryListWithIndices}`
  },
  {
    role: 'user',
    content: 'Make these memories feel personal and conversational. Show 2-3 best memories per category. Return exactly 2-3 memories per category when possible. Return only the JSON with indices.'
  }
], {
  temperature: 0.5,
  max_tokens: 1200 // ✅ INCREASED: Allow for more memories
});
    let content = response.choices?.[0]?.message?.content;
    
    if (!content || content.trim().length === 0) {
      throw new Error('Empty AI response');
    }
    
    content = content.trim();
    
    // ✅ Extract JSON more robustly
    if (content.startsWith('```json')) {
      content = content.replace(/^```json\s*/, '').replace(/```\s*$/, '');
    } else if (content.startsWith('```')) {
      content = content.replace(/^```\s*/, '').replace(/```\s*$/, '');
    }
    
    const parsed = JSON.parse(content.trim());
    
    if (typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('Invalid response structure');
    }
    
    // ✅ NEW: Map using index, with better fallbacks
    const createMemoryWithId = (item) => {
      const index = item.index;
      const memoryText = item.text || item;
      
      let originalMemory;
      
      if (typeof index === 'number' && topMemories[index]) {
        // ✅ PERFECT: Use the index directly!
        originalMemory = topMemories[index];
        console.log(`✅ Matched by index: ${index} - "${memoryText.substring(0, 40)}"`);
      } else {
        // ✅ FALLBACK 1: Try text matching from top memories
        originalMemory = topMemories.find(m => m.memory === memoryText);
        
        if (!originalMemory) {
          // ✅ FALLBACK 2: Try text matching from all memories
          originalMemory = memories.find(m => m.memory === memoryText);
        }
        
        if (!originalMemory) {
          // ✅ FALLBACK 3: Try fuzzy match
          const keywords = memoryText.toLowerCase().split(' ').filter(w => w.length > 4);
          originalMemory = topMemories.find(m => {
            const memoryLower = m.memory.toLowerCase();
            return keywords.some(keyword => memoryLower.includes(keyword));
          });
        }
        
        if (originalMemory) {
          console.log(`✅ Matched by text: "${memoryText.substring(0, 40)}"`);
        }
      }
      
      if (!originalMemory) {
        console.warn('⚠️ Could not match memory:', memoryText.substring(0, 40));
      }
      
      return {
        text: typeof memoryText === 'string' ? memoryText : memoryText.text || '',
        id: originalMemory?.id || null,
        originalText: originalMemory?.memory || null,
        fullMemory: originalMemory || null
      };
    };
    
    const validCategories = {
      'Things You\'re Working On': (parsed['Things You\'re Working On'] || []).map(createMemoryWithId),
      'What You Love': (parsed['What You Love'] || []).map(createMemoryWithId),
      'How You Roll': (parsed['How You Roll'] || []).map(createMemoryWithId)
    };
    
    // ✅ Validate all memories have IDs
    const totalMemories = Object.values(validCategories).flat();
    const memoriesWithIds = totalMemories.filter(m => m.id);
    const memoriesWithoutIds = totalMemories.filter(m => !m.id);
    
    console.log('🔍 Memory ID validation:', {
      total: totalMemories.length,
      withIds: memoriesWithIds.length,
      withoutIds: memoriesWithoutIds.length,
      missingIds: memoriesWithoutIds.map(m => m.text.substring(0, 30))
    });
    
    memoryCacheRef.current = validCategories;
    cacheHashRef.current = currentHash;
    
    setStructuredMemories(validCategories);
    console.log('✅ Memories structured successfully:', {
      'Things You\'re Working On': validCategories['Things You\'re Working On'].length,
      'What You Love': validCategories['What You Love'].length,
      'How You Roll': validCategories['How You Roll'].length
    });
    
  } catch (error) {
    console.error('Error structuring memories:', error);
    
    if (memoryCacheRef.current) {
      setStructuredMemories(memoryCacheRef.current);
    } else {
      setStructuredMemories({
        'Things You\'re Working On': [],
        'What You Love': [],
        'How You Roll': []
      });
    }
    
    setCanRefreshAI(true);
    setCooldownRemaining(0);
  } finally {
    setIsLoadingProfile(false);
  }
};

// ============================================
// FIXED: handleAskQuestion() - IMPROVED CHAT WITH FULL MEMORY CONTEXT
// ============================================

const handleAskQuestion = async () => {
  if (!chatInput.trim() || !selectedCategory) return;

  const userMessage = {
    role: 'user',
    text: chatInput,
    timestamp: new Date()
  };

  const newHistory = [...chatHistory, userMessage];
  setChatHistory(newHistory);
  setChatInput('');
  setIsChatLoading(true);

  try {
    const categoryMemories = structuredMemories[selectedCategory] || [];
    
    // ✅ IMPROVED: Extract ALL memory text from the category
    const categoryMemoryTexts = categoryMemories.map(m => 
      typeof m === 'string' ? m : m.text
    );
    
    // ✅ NEW: Also get the original full memory objects for context
    const fullCategoryMemories = categoryMemories
      .filter(m => m.fullMemory)
      .map(m => m.fullMemory.memory);
    
    // ✅ IMPORTANT: Include ALL memories from this category, not just first 3
    const categoryContext = categoryMemoryTexts.length > 0 
      ? categoryMemoryTexts.join('\n')
      : 'No memories in this category yet.';
    
    // ✅ NEW: Add ALL user memories for reference (not just category)
    let allMemoriesContext = 'All memories:\n';
    Object.entries(structuredMemories).forEach(([cat, memories]) => {
      if (memories && memories.length > 0) {
        const texts = memories.map(m => typeof m === 'string' ? m : m.text);
        allMemoriesContext += `\n${cat}:\n`;
        allMemoriesContext += texts.join('\n');
      }
    });

    console.log('🧠 Chat context prepared:', {
      selectedCategory,
      categorMemories: categoryMemoryTexts.length,
      allMemories: Object.values(structuredMemories).flat().length,
      userQuestion: chatInput.substring(0, 50)
    });

    // ✅ CRITICAL FIX: Use SecureAPI correctly
    const response = await SecureAPI.callAzureOpenAI(
      [
        {
          role: 'system',
          content: `You are Preptune, a chill AI chat buddy built and owned by Preptune. You're designed to be a personalized friend who remembers and understands what matters to the user.

${userName ? `The user's name is ${userName}.` : ''}

---

## CURRENT CONVERSATION CONTEXT: "${selectedCategory}"

### Memories in this category:
${categoryContext}

### All known memories (for cross-reference):
${allMemoriesContext}

---

## YOUR ROLE:
- You know this user deeply through their memories
- Answer questions about their life, goals, habits, and interests
- Be warm, conversational, and genuinely interested
- Reference their specific memories when relevant
- Help them explore patterns and insights about themselves
- Be supportive and non-judgmental

## TONE:
- Sound like a close friend, not a robot
- Short, natural responses (1-3 sentences usually)
- Match their energy and communication style
- Use their names/specific details when relevant

## CRITICAL RULES:
- NEVER say "I don't remember" - you have their memories right here
- NEVER make up memories they didn't mention
- If they ask about something not in their memories, say so naturally
- Always be respectful and honest
- Help them gain insights from patterns in their own memories`
        },
        ...newHistory.map(msg => ({
          role: msg.role,
          content: msg.text
        }))
      ],
      {
        temperature: 0.7,
        max_tokens: 300 // ✅ INCREASED: Allow longer responses
      }
    );
    
    const assistantMessage = {
      role: 'assistant',
      text: response.choices[0].message.content,
      timestamp: new Date()
    };
    setChatHistory(prev => [...prev, assistantMessage]);
    
    console.log('✅ Chat response generated');
    
  } catch (error) {
    console.error('❌ Chat error:', error);
    const errorMessage = {
      role: 'assistant',
      text: "Sorry, I'm having trouble responding right now. Please try again.",
      timestamp: new Date()
    };
    setChatHistory(prev => [...prev, errorMessage]);
  } finally {
    setIsChatLoading(false);
  }
};


  // ============================================
  // MEMORY STRUCTURING WITH COOLDOWN & CACHE
  // ============================================

  const getMemoryHash = (memories) => {
    return memories.map(m => m.id).sort().join(',');
  };



  const handleManualRefresh = () => {
    if (canRefreshAI) {
      hasStructuredRef.current = false;
      structureMemories(true);
    }
  };

  // ============================================
  // ICONS
  // ============================================

const getCategoryIcon = (category) => {
  if (category === 'Things You\'re Working On') {
    return (
      <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7.49985 0.877045C3.84216 0.877045 0.877014 3.84219 0.877014 7.49988C0.877014 11.1575 3.84216 14.1227 7.49985 14.1227C11.1575 14.1227 14.1227 11.1575 14.1227 7.49988C14.1227 3.84219 11.1575 0.877045 7.49985 0.877045ZM1.82701 7.49988C1.82701 4.36686 4.36683 1.82704 7.49985 1.82704C10.6328 1.82704 13.1727 4.36686 13.1727 7.49988C13.1727 10.6329 10.6328 13.1727 7.49985 13.1727C4.36683 13.1727 1.82701 10.6329 1.82701 7.49988ZM7.49999 9.49999C8.60456 9.49999 9.49999 8.60456 9.49999 7.49999C9.49999 6.39542 8.60456 5.49999 7.49999 5.49999C6.39542 5.49999 5.49999 6.39542 5.49999 7.49999C5.49999 8.60456 6.39542 9.49999 7.49999 9.49999Z" fill="#6b6b6b" fillRule="evenodd" clipRule="evenodd"/>
      </svg>
    );
  } else if (category === 'What You Love') {
    return <Star size={16} className="category-icon" />;
  } else if (category === 'How You Roll') {
    return <Coffee size={16} className="category-icon" />;
  }
  return <Brain size={16} className="category-icon" />;
};

  // ============================================
  // MEMORY MANAGEMENT
  // ============================================

const handleDeleteMemoryClick = (memoryText, memoryId) => {
  setMemoryToDelete(memoryText);
  setShowDeleteMemoryConfirm(true);
  
  // ✅ Store the ID so we can actually delete it
  setDeletingMemoryId(memoryId);
};

const confirmDeleteMemory = async () => {
  try {
    if (!deletingMemoryId && !memoryToDelete) {
      console.error('No memory selected for deletion');
      return;
    }

    console.log('🗑️ Deleting memory:', memoryToDelete);
    
    let memoryItemToDelete = null;
    
    if (deletingMemoryId) {
      memoryItemToDelete = memories.find(m => m.id === deletingMemoryId);
    } else {
      memoryItemToDelete = memories.find(m => m.memory === memoryToDelete);
    }
    
    if (!memoryItemToDelete) {
      console.error('Memory not found:', memoryToDelete);
      setShowDeleteMemoryConfirm(false);
      return;
    }

    const memoryIdToDelete = memoryItemToDelete.id;
    
    console.log('Found memory to delete:', memoryIdToDelete);

    if (!firestoreService?.memoryService) {
      console.error('❌ Memory service not available');
      throw new Error('Memory service not initialized');
    }

    // ✅ STEP 1: Close modal immediately
    setShowDeleteMemoryConfirm(false);
    
    // ✅ STEP 2: Mark as deleting (triggers fade-out animation)
    setDeletingMemoryIds(prev => new Set([...prev, memoryIdToDelete]));
    
    // ✅ STEP 3: Wait for animation (300ms)
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // ✅ STEP 4: Remove from UI state (triggers slide-up animation)
    setMemories(prev => prev.filter(m => m.id !== memoryIdToDelete));
    
    // ✅ STEP 5: Update structured memories to remove the deleted item
    setStructuredMemories(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(category => {
        updated[category] = updated[category].filter(item => {
          const itemId = typeof item === 'object' ? item.id : null;
          return itemId !== memoryIdToDelete;
        });
      });
      return updated;
    });
    
    // ✅ STEP 6: Delete from Firestore in background
    try {
      await firestoreService.memoryService.deleteMemory(memoryIdToDelete);
      console.log('✅ Memory deleted from Firestore');
    } catch (firestoreError) {
      console.error('❌ Firestore deletion failed:', firestoreError);
      // ✅ Rollback UI if Firestore fails
      alert('Failed to delete from server. Refreshing...');
      await loadMemories(); // Reload to get accurate state
      return;
    }
    
    // ✅ STEP 7: Clear deletion state
    setDeletingMemoryIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(memoryIdToDelete);
      return newSet;
    });
    
    // ✅ STEP 8: Clear cache so it refreshes properly
    memoryCacheRef.current = null;
    cacheHashRef.current = null;
    hasStructuredRef.current = false;
    
    // ✅ STEP 9: Reset delete state
    setMemoryToDelete(null);
    setDeletingMemoryId(null);
    
    console.log('✅ Memory deleted successfully with animation');
    
  } catch (error) {
    console.error('❌ Error deleting memory:', error);
    alert('Failed to delete memory. Please try again.');
    
    // Reset states
    setShowDeleteMemoryConfirm(false);
    setMemoryToDelete(null);
    setDeletingMemoryId(null);
    setDeletingMemoryIds(new Set());
  }
};


const handleConfirmClear = async () => {
  try {
    if (!firestoreService?.memoryService) {
      console.error('Memory service not available');
      return;
    }

    console.log('🗑️ Clearing all memories...');
    
    // ✅ STEP 1: Close modal and start clearing state
    setShowClearConfirm(false);
    setIsClearingAll(true);
    
    // ✅ STEP 2: Get all memory IDs
    const allMemoryIds = memories.map(m => m.id);
    
    // ✅ STEP 3: Animate deletion one by one (fast)
    for (let i = 0; i < allMemoryIds.length; i++) {
      const memoryId = allMemoryIds[i];
      
      // Mark as deleting
      setDeletingMemoryIds(prev => new Set([...prev, memoryId]));
      
      // Short delay for staggered animation
      await new Promise(resolve => setTimeout(resolve, 30));
      
      // Remove from UI
      setMemories(prev => prev.filter(m => m.id !== memoryId));
    }
    
    // ✅ STEP 4: Clear structured memories
    setStructuredMemories({
      'Things You\'re Working On': [],
      'What You Love': [],
      'How You Roll': []
    });
    
    // ✅ STEP 5: Delete from Firestore in background (batch)
    console.log(`Deleting ${allMemoryIds.length} memories from Firestore...`);
    
    try {
      await Promise.all(
        allMemoryIds.map(id => 
          firestoreService.memoryService.deleteMemory(id)
        )
      );
      console.log('✅ All memories deleted from Firestore');
    } catch (firestoreError) {
      console.error('❌ Some Firestore deletions failed:', firestoreError);
      alert('Some memories failed to delete. Refreshing...');
      await loadMemories();
      setIsClearingAll(false);
      return;
    }
    
    // ✅ STEP 6: Clear all state
    setMemories([]);
    setDeletingMemoryIds(new Set());
    
    // Clear cache
    memoryCacheRef.current = null;
    cacheHashRef.current = null;
    hasStructuredRef.current = false;
    
    // ✅ STEP 7: Wait a moment for user feedback
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setIsClearingAll(false);
    
    console.log('✅ All memories cleared with animation');
    
  } catch (error) {
    console.error('❌ Error clearing memories:', error);
    alert('Failed to clear memories. Please try again.');
    setIsClearingAll(false);
    setDeletingMemoryIds(new Set());
  }
};


  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  const handleNicknameUpdate = async () => {
    if (!nickname.trim()) {
      setNicknameError('Nickname cannot be empty');
      return;
    }

    if (nickname.length > 50) {
      setNicknameError('Nickname must be less than 50 characters');
      return;
    }

    try {
      setIsSavingNickname(true);
      setNicknameError('');

      if (firestoreService) {
        await firestoreService.updateUserData({
          'settings.userName': nickname.trim()
        });
      }

      if (onNicknameUpdate) {
        onNicknameUpdate(nickname.trim());
      }

      setIsEditingNickname(false);
    } catch (error) {
      setNicknameError('Failed to save nickname');
    } finally {
      setIsSavingNickname(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    
    if (type === 'id') {
      setShowIdCopyFeedback(true);
      setTimeout(() => setShowIdCopyFeedback(false), 2000);
    } else if (type === 'email') {
      setShowEmailCopyFeedback(true);
      setTimeout(() => setShowEmailCopyFeedback(false), 2000);
    }
  };

const handleClose = () => {
  setIsAnimatingOut(true);
  
  // Adjust timeout based on screen size
  const isTablet = window.innerWidth >= 768;
  const animationDuration = isTablet ? 250 : 300;
  
  setTimeout(() => {
    onClose();
    setIsAnimatingOut(false);
  }, animationDuration);
};

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const formatCooldownTime = (ms) => {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  if (!isOpen) return null;

  // ============================================
  // RENDER
  // ============================================

  return (
    <>
      <div 
        className={`settings-overlay ${isAnimatingOut ? 'animating-out' : ''}`}
        onClick={handleClose}
      />

      <div 
        className={`settings-container ${isAnimatingOut ? 'slide-down' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* TAB NAVIGATION */}
        <div className="settings-tabs">
          <button 
            className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button 
            className={`settings-tab ${activeTab === 'memories' ? 'active' : ''}`}
            onClick={() => setActiveTab('memories')}
          >
            Memories
          </button>
        </div>

        {/* ============================================
            PROFILE TAB
            ============================================ */}
        {activeTab === 'profile' && (
          <div className="settings-scroll">
            <div className="settings-header1">
              <h4 className="voice-modal-title">Your Profile</h4>
              <button className="settings-close-btn" onClick={handleClose}>
                <X size={20} />
              </button>
            </div>

            <section className="settings-section">
              <div className="settings3-card">
                <div className="card-header1">
                  <label className="card-label">Display Name</label>
                  {!isEditingNickname && (
                    <button
                      onClick={() => {
                        setIsEditingNickname(true);
                        setNickname(userName || '');
                        setNicknameError('');
                      }}
                      className="white-btn-primary"
                    >
                      Edit
                    </button>
                  )}
                </div>

                {isEditingNickname ? (
                  <div className="edit-container">
                    <input
                      type="text"
                      value={nickname}
                      onChange={(e) => {
                        setNickname(e.target.value);
                        setNicknameError('');
                      }}
                      placeholder="Enter your display name"
                      className="settings-input"
                      maxLength={50}
                      autoFocus
                    />
                    {nicknameError && (
                      <p className="error-text">{nicknameError}</p>
                    )}
                    <p className="char-count">{nickname.length}/50 characters</p>
                    <div className="button-group">
                      <button
                        onClick={() => {
                          setIsEditingNickname(false);
                          setNickname(userName || '');
                          setNicknameError('');
                        }}
                        className="white-btn"
                        disabled={isSavingNickname}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleNicknameUpdate}
                        className="white-btn-primary"
                        disabled={isSavingNickname}
                      >
                        {isSavingNickname ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="value-display">
                    <span className="value-text">{userName || 'Not set'}</span>
                  </div>
                )}
              </div>
            </section>

            <section className="settings-section">
              <h3 className="section-title">Account</h3>

              <div className="settings3-card">
                <label className="card-label">
<svg width="13" height="13" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.94721 0.164594C7.66569 0.0238299 7.33431 0.0238302 7.05279 0.164594L0.552786 3.41459C0.214002 3.58399 0 3.93025 0 4.30902V12C0 12.5523 0.447715 13 1 13H14C14.5523 13 15 12.5523 15 12V4.30902C15 3.93025 14.786 3.58399 14.4472 3.41459L7.94721 0.164594ZM13.5689 4.09349L7.5 1.05902L1.43105 4.09349L7.5 7.29136L13.5689 4.09349ZM1 4.88366V12H14V4.88366L7.70977 8.19813C7.57848 8.26731 7.42152 8.26731 7.29023 8.19813L1 4.88366Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>                  Email Address
                </label>
                <div className="value-container">
                  <span className="value-text">{userEmail}</span>
                  <button
                    onClick={() => copyToClipboard(userEmail, 'email')}
                    className={`white-btn ${showEmailCopyFeedback ? 'active' : ''}`}
                  >
                    {showEmailCopyFeedback ? (
                      <>
                        <Check size={14} />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy size={14} />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="settings3-card">
                <label className="card-label">
                  <Shield size={14} />
                  Preptune ID
                </label>
                <div className="value-container">
                  <span className={`value-text monospace ${!showRevealId ? 'blurred-id' : ''}`}>
                    {preptuneId}
                  </span>
                  <button
                    onClick={() => setShowRevealId(!showRevealId)}
                    className="white-btn"
                  >
                    {showRevealId ? 'Hide' : 'Show'}
                  </button>
                  <button
                    onClick={() => copyToClipboard(preptuneId, 'id')}
                    className={`white-btn ${showIdCopyFeedback ? 'active' : ''}`}
                  >
                    {showIdCopyFeedback ? (
                      <>
                        <Check size={14} />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy size={14} />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

<div className="settings3-card">
  <label className="card-label">
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 2.75C8 2.47386 7.77614 2.25 7.5 2.25C7.22386 2.25 7 2.47386 7 2.75V7H2.75C2.47386 7 2.25 7.22386 2.25 7.5C2.25 7.77614 2.47386 8 2.75 8H7V12.25C7 12.5261 7.22386 12.75 7.5 12.75C7.77614 12.75 8 12.5261 8 12.25V8H12.25C12.5261 8 12.75 7.77614 12.75 7.5C12.75 7.22386 12.5261 7 12.25 7H8V2.75Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
    </svg>
    Voice
  </label>
  
  {/* Mobile: Show simple button that opens VoiceModal */}
  <div className="voice-selector-mobile">
    <div className="value-container">
      <span className="value-text">
        {selectedVoice === 'Host 1' && 'Emma'}
        {selectedVoice === 'Host 2' && 'James'}
        {selectedVoice === 'Host 3' && 'Jackie'}
        {selectedVoice === 'Host 4' && 'Kenny'}
      </span>
      <button 
        className="white-btn"
        onClick={() => setShowVoiceChangeModal(true)}
      >
        Change
      </button>
    </div>
  </div>

  {/* Desktop/Tablet: Show dropdown with preview */}
  <div className="voice-selector-desktop">
    <div className="voice-display-row">
      <span className="voice-display-label">Select Voice</span>
      <div className="voice-action-buttons">
<button 
  className={`voice-play-btn ${isPlayingPreview ? 'playing' : ''}`}
  onClick={() => handlePlayVoicePreview(selectedVoice)}
>
  {isPlayingPreview ? (
    <>
<svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
  <path d="M7 5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H7Z"/>
</svg>

      Stop
    </>
  ) : (
    <>
<svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
  <path fill-rule="evenodd" d="M8.6 5.2A1 1 0 0 0 7 6v12a1 1 0 0 0 1.6.8l8-6a1 1 0 0 0 0-1.6l-8-6Z" clip-rule="evenodd"/>
</svg>

      Play
    </>
  )}
</button>
        <button 
          className="voice-change-btn"
          onClick={() => setShowVoiceDropdown(!showVoiceDropdown)}
        >
          {selectedVoice === 'Host 1' && 'Emma'}
          {selectedVoice === 'Host 2' && 'James'}
          {selectedVoice === 'Host 3' && 'Jackie'}
          {selectedVoice === 'Host 4' && 'Kenny'}
          <svg width="14" height="14" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className={`dropdown-chevron ${showVoiceDropdown ? 'rotated' : ''}`}>
            <path d="M4.18179 6.18181C4.35753 6.00608 4.64245 6.00608 4.81819 6.18181L7.49999 8.86362L10.1818 6.18181C10.3575 6.00608 10.6424 6.00608 10.8182 6.18181C10.9939 6.35755 10.9939 6.64247 10.8182 6.81821L7.81819 9.81821C7.73379 9.9026 7.61934 9.95001 7.49999 9.95001C7.38064 9.95001 7.26618 9.9026 7.18179 9.81821L4.18179 6.81821C4.00605 6.64247 4.00605 6.35755 4.18179 6.18181Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</div>

{/* Dropdown Overlay for Desktop/Tablet */}
{showVoiceDropdown && (
  <>
    <div className="voice-dropdown-overlay" onClick={() => setShowVoiceDropdown(false)} />
    <div className="voice-dropdown-popup">
      <div className="voice-popup-header">
        <h3 className="voice-popup-title">Select Voice</h3>
        <button 
          className="voice-popup-close"
          onClick={() => setShowVoiceDropdown(false)}
        >
          <X size={16} />
        </button>
      </div>
      <div className="voice-dropdown-menu">
        {[
          { id: 'Host 1', name: 'Emma', personality: 'Warm & friendly', audioFile: '/sound/voice1.wav' },
          { id: 'Host 2', name: 'James', personality: 'Upbeat & energetic', audioFile: '/sound/bob-voice.wav' },
          { id: 'Host 3', name: 'Jackie', personality: 'Relaxed & easygoing', audioFile: '/sound/jackie-voice.wav' },
          { id: 'Host 4', name: 'Kenny', personality: 'Smooth & conversational', audioFile: '/sound/alex-voice.wav' }
        ].map((voice) => (
          <div
            key={voice.id}
            className={`voice-dropdown-item ${selectedVoice === voice.id ? 'selected' : ''}`}
          >
            <div 
              className="voice-item-content"
              onClick={() => handleVoiceSelect(voice.id)}
            >
              <div className="voice-item-info">
                <span className="voice-item-name-inline">
                  {voice.name} <span className="voice-separator">|</span> <span className="voice-personality-inline">{voice.personality}</span>
                </span>
              </div>
              {selectedVoice === voice.id && (
                <svg width="18" height="18" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="voice-checkmark">
                  <path d="M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
                </svg>
              )}
            </div>
            <button
              className={`voice-play-btn ${isPlayingPreview && playingVoiceId === voice.id ? 'playing' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handlePlayVoicePreview(voice.id);
              }}
            >
              {isPlayingPreview && playingVoiceId === voice.id ? (
                <>
                  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M7 5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H7Z"/>
                  </svg>
                  Stop
                </>
              ) : (
                <>
                  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M8.6 5.2A1 1 0 0 0 7 6v12a1 1 0 0 0 1.6.8l8-6a1 1 0 0 0 0-1.6l-8-6Z" clipRule="evenodd"/>
                  </svg>
                  Play
                </>
              )}
            </button>
          </div>
        ))}
      </div>
    </div>
  </>
)}

            </section>

            <section className="settings-section">
              <div className="info-box">
                <p>Your information is secure and private. Only you can view and manage it.</p>
              </div>
            </section>
          </div>
        )}

        {/* ============================================
            MEMORIES TAB
            ============================================ */}
        {activeTab === 'memories' && (
          <div className={`settings-scroll ${memories.length > 0 ? 'with-fixed-button' : ''}`}>
            <div className="settings-header1">
              <h4 className="voice-modal-title">What I Know About You</h4>
              <button className="settings-close-btn" onClick={handleClose}>
                <X size={20} />
              </button>
            </div>

            {/* CHAT MODE */}
            {chatMode ? (
              <div className="memory-chat-container">
                <div className="chat-header">
                  <button 
                    onClick={() => {
                      setChatMode(false);
                      setChatHistory([]);
                      setChatInput('');
                      setSelectedCategory(null);
                    }}
                    className="chat-back-btn"
                  >
<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.84182 3.13514C9.04327 3.32401 9.05348 3.64042 8.86462 3.84188L5.43521 7.49991L8.86462 11.1579C9.05348 11.3594 9.04327 11.6758 8.84182 11.8647C8.64036 12.0535 8.32394 12.0433 8.13508 11.8419L4.38508 7.84188C4.20477 7.64955 4.20477 7.35027 4.38508 7.15794L8.13508 3.15794C8.32394 2.95648 8.64036 2.94628 8.84182 3.13514Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>                  </button>
                  <h3 className="chat-title">{selectedCategory}</h3>
                </div>

                <div className="chat-messages">
                  {chatHistory.length === 0 && (
                    <div className="chat-empty">
                      <MessageCircle size={24} style={{ opacity: 0.3 }} />
                      <p>Ask about {selectedCategory.toLowerCase()}</p>
                    </div>
                  )}
                  {chatHistory.map((msg, idx) => (
                    <div key={idx} className={`chat-message ${msg.role}`}>
                      <span className="chat-text">{msg.text}</span>
                    </div>
                  ))}
                  {isChatLoading && (
                    <div className="chat-message assistant">
                      <span className="chat-text">Thinking...</span>
                    </div>
                  )}
                </div>

                <div className="chat-input-area">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAskQuestion()}
                    placeholder="Ask a question..."
                    className="chat-input"
                    disabled={isChatLoading}
                  />
                  <button
                    onClick={handleAskQuestion}
                    className="chat-send-btn"
                    disabled={isChatLoading || !chatInput.trim()}
                  >
                    <svg width="14" height="14" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.13523 8.84197C3.3241 9.04343 3.64052 9.05363 3.84197 8.86477L7.5 5.43536L11.158 8.86477C11.3595 9.05363 11.6759 9.04343 11.8648 8.84197C12.0536 8.64051 12.0434 8.32409 11.842 8.13523L7.84197 4.38523C7.64964 4.20492 7.35036 4.20492 7.15803 4.38523L3.15803 8.13523C2.95657 8.32409 2.94637 8.64051 3.13523 8.84197Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* LOADING STATE */}
                {isLoadingProfile && (
                  <div className="memory-loading-container">
                    <div className="simple-spinner"></div>
                    <p className="memory-loading-text">Organizing memories...</p>
                  </div>
                )}

                {/* STRUCTURED MEMORIES - EXPANDABLE */}
{!isLoadingProfile && (
  <section className="settings-section">
    {Object.keys(structuredMemories).length === 0 || 
     Object.values(structuredMemories).every(items => !items || items.length === 0) ? (
      /* ALL CATEGORIES EMPTY - Show main empty state */
      <div className="memories-empty-state">
        <svg width="40" height="40" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.3, marginBottom: '16px' }}>
          <path d="M2 5.5C2 4.11929 3.11929 3 4.5 3C5.06291 3 5.58237 3.18604 6.00024 3.5H9.00024C9.41811 3.18604 9.93757 3 10.5 3C11.8807 3 13 4.11929 13 5.5C13 5.67733 12.9843 5.85104 12.9542 6.02036C13.591 6.18974 14 6.74298 14 7.5C14 8.32843 13.3284 9 12.5 9H2.5C1.67157 9 1 8.32843 1 7.5C1 6.74298 1.40896 6.18974 2.04583 6.02036C2.01569 5.85104 2 5.67733 2 5.5ZM4.5 4C3.67157 4 3 4.67157 3 5.5C3 5.67099 3.02524 5.83652 3.07149 5.99307L3.20117 6.43814L2.73018 6.48521C2.31613 6.52543 2 6.87718 2 7.5C2 7.77614 2.22386 8 2.5 8H12.5C12.7761 8 13 7.77614 13 7.5C13 6.87718 12.6839 6.52543 12.2698 6.48521L11.7988 6.43814L11.9285 5.99307C11.9748 5.83652 12 5.67099 12 5.5C12 4.67157 11.3284 4 10.5 4C10.0691 4 9.68679 4.18805 9.42426 4.5H5.57574C5.31321 4.18805 4.93094 4 4.5 4Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
        </svg>
        <h3 style={{ fontSize: '15px', fontWeight: '500', margin: '0 0 6px 0', color: '#2d3748' }}>
          No memories yet
        </h3>
        <p style={{ fontSize: '13px', color: '#718096', margin: '0', lineHeight: '1.6', maxWidth: '300px' }}>
          Your conversations will be saved here so you can revisit and manage what's important to you
        </p>
      </div>
    ) : (
      /* SHOW CATEGORIES */
      Object.entries(structuredMemories).map(([category, items]) => (
        <div key={category} className="memory-category-card">
          <button
            className="memory-category-header"
            onClick={() => toggleCategory(category)}
          >
            <div className="category-left">
              {getCategoryIcon(category)}
              <span className="memory-category-name">{category}</span>
              {/* ✅ Show count badge if category has items */}
              {items && items.length > 0 && (
                <span className="memory-count-badge">{items.length}</span>
              )}
            </div>
            <ChevronRight 
              size={18} 
              className={`category-chevron ${expandedCategories[category] ? 'rotated' : ''}`}
            />
          </button>
{expandedCategories[category] && (
  <div className="memory-items-expanded">
    {items && items.length > 0 ? (
      <>
{items.slice(0, 2).map((memoryItem, idx) => {
  const memoryText = typeof memoryItem === 'string' ? memoryItem : memoryItem.text;
  const memoryId = typeof memoryItem === 'object' ? memoryItem.id : null;
  const finalId = memoryId || memories.find(m => m.memory === memoryText)?.id;
  
  const isDeleting = deletingMemoryIds.has(finalId);
  const isEditing = editingMemoryId === finalId;
  
  if (!finalId) {
    console.warn('⚠️ Memory missing ID:', memoryText);
  }
  
  return (
    <div 
      key={finalId || idx} 
      className={`memory-item ${isDeleting ? 'deleting' : ''} ${isEditing ? 'editing' : ''}`}
      style={{
        opacity: isDeleting ? 0 : 1,
        transform: isDeleting ? 'translateX(-20px)' : 'translateX(0)',
        transition: 'all 0.3s ease-out',
        maxHeight: isDeleting ? '0' : 'none',
        overflow: 'visible'
      }}
    >
      {isEditing ? (
        <div className="memory-edit-container">
          <textarea
            value={editingMemoryText}
            onChange={(e) => setEditingMemoryText(e.target.value)}
            className="memory-edit-input"
            autoFocus
            onFocus={(e) => {
              e.target.selectionStart = e.target.value.length;
              e.target.selectionEnd = e.target.value.length;
            }}
          />
          <div className="memory-edit-actions">
            <button
              onClick={handleCancelEdit}
              className="memory-edit-cancel"
              disabled={isSavingMemory}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              className="memory-edit-save"
              disabled={isSavingMemory || !editingMemoryText.trim()}
            >
              {isSavingMemory ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <span className="memory-text">{memoryText}</span>
          <div className="memory-actions">
            <button
              onClick={() => {
                if (finalId && !isDeleting) {
                  handleEditMemoryClick(memoryText, finalId);
                }
              }}
              className="memory-edit-btn"
              title="Edit memory"
              disabled={!finalId || isDeleting}
              style={{ 
                opacity: finalId && !isDeleting ? 1 : 0.5,
                cursor: finalId && !isDeleting ? 'pointer' : 'not-allowed'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.8536 1.14645C11.6583 0.951184 11.3417 0.951184 11.1465 1.14645L3.71455 8.57836C3.62459 8.66832 3.55263 8.77513 3.50251 8.89299L2.04044 12.303C1.9599 12.491 2.00189 12.709 2.14646 12.8536C2.29103 12.9981 2.50905 13.0401 2.69697 12.9596L6.10701 11.4975C6.22487 11.4474 6.33168 11.3754 6.42164 11.2855L13.8536 3.85355C14.0488 3.65829 14.0488 3.34171 13.8536 3.14645L11.8536 1.14645ZM4.42166 9.28547L11.5 2.20711L12.7929 3.5L5.71455 10.5784L4.21924 11.2192L3.78081 10.7808L4.42166 9.28547Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
              </svg>
            </button>
            <button
              onClick={() => {
                if (finalId && !isDeleting) {
                  handleDeleteMemoryClick(memoryText, finalId);
                } else if (!finalId) {
                  console.error('❌ Cannot delete - memory has no ID');
                  alert('Error: This memory cannot be deleted. Please refresh and try again.');
                }
              }}
              className="memory-delete-btn"
              title="Delete memory"
              disabled={!finalId || isDeleting}
              style={{ 
                opacity: finalId && !isDeleting ? 1 : 0.5,
                cursor: finalId && !isDeleting ? 'pointer' : 'not-allowed'
              }}
            >
              <X size={14} />
            </button>
          </div>
        </>
      )}
    </div>
  );
})}
        <button
          onClick={() => {
            setChatMode(true);
            setSelectedCategory(category);
            setChatHistory([]);
            setChatInput('');
          }}
          className="memory-ask-btn"
        >
          Ask more questions
        </button>
      </>
    ) : (
      <div className="category-empty-state">
        <p>Nothing here yet. Keep chatting to build memories in this area!</p>
      </div>
    )}
  </div>
)}



        </div>
      ))
    )}
  </section>
)}

<style jsx>{`
  .memories-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 50px 20px;
  }

  .category-empty-state {
    padding: 20px;
    text-align: center;
  }

  .category-empty-state p {
    font-size: 13px;
    color: #718096;
    margin: 0;
  }
/* Memory deletion animations */
.memory-item {
  animation: slideInMemory 0.3s ease-out;
  transition: all 0.3s ease-out;
}

.memory-item.deleting {
  opacity: 0 !important;
  transform: translateX(-20px) !important;
  max-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
}

@keyframes slideInMemory {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Clearing all animation */
.memory-category-card.clearing {
  animation: fadeOutCard 0.4s ease-out forwards;
}

@keyframes fadeOutCard {
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.95);
  }
}

/* Clear all progress indicator */
.clearing-progress {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  z-index: 10000;
  text-align: center;
}

.clearing-progress-text {
  margin-top: 16px;
  font-size: 14px;
  color: #718096;
  font-weight: 500;
}
  .memory-count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    background: #e2e8f0;
    color: #4a5568;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    margin-left: 8px;
  }

  .memory-item {
    animation: slideIn 0.2s ease-out;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .simple-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e2e8f0;
    border-top-color: #a0aec0;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`}</style>

<style jsx>{`
  .memories-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 50px 20px;
  }

  .category-empty-state {
    padding: 20px;
    text-align: center;
  }

  .category-empty-state p {
    font-size: 13px;
    color: #718096;
    margin: 0;
  }
`}</style>



{/* Add these minimal styles */}
<style jsx>{`
  .memories-empty-state {
    padding: 40px 20px;
    text-align: center;
  }

  .empty-state-text {
    font-size: 14px;
    color: #718096;
    margin: 0;
    line-height: 1.5;
  }

  .category-empty-state {
    padding: 16px;
    text-align: center;
  }

  .category-empty-state p {
    font-size: 13px;
    color: #718096;
    margin: 0;
  }
`}</style>
{/* Add these styles to your SettingsModal.css or inline styles */}
<style jsx>{`
  .memories-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 60px 20px;
  }

  .category-empty-state {
    padding: 20px;
    text-align: center;
  }

  .category-empty-state p {
    font-size: 13px;
    color: #718096;
    margin: 0;
    font-style: italic;
  }

  .memory-count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    background: #e2e8f0;
    color: #4a5568;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    margin-left: 8px;
  }
    /* ============================================
   VOICE SELECTOR DROPDOWN
   ============================================ */

.voice-selector-container {
  position: relative;
  width: 100%;
}

.voice-display-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #fff;
  border-radius: 16px;
  padding: 12px;
  border: 1px solid #f0f0f0;
}

.voice-display-name {
  font-size: 14px;
  color: #333;
  font-weight: 400;
  flex: 1;
}

.voice-action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.voice-play-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 14px;
  background: #f5f5f5;
  border: none;
  border-radius: 20px;
  color: #333;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.voice-play-btn:hover:not(:disabled) {
  background: #e8e8e8;
  transform: translateY(-1px);
}

.voice-play-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.voice-change-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #1e76f2 0%, #86bfff 100%);
  border: 1px solid rgba(238, 238, 238, 0.3);
  border-radius: 20px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(30, 118, 242, 0.25);
  white-space: nowrap;
}

.voice-change-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(30, 118, 242, 0.35);
}

.dropdown-chevron {
  transition: transform 0.2s ease;
}

.dropdown-chevron.rotated {
  transform: rotate(180deg);
}

.voice-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  overflow: hidden;
  animation: dropdownSlideIn 0.2s ease-out;
}

@keyframes dropdownSlideIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.voice-dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f5f5f5;
}

.voice-dropdown-item:last-child {
  border-bottom: none;
}

.voice-dropdown-item:hover {
  background: #fafafa;
}

.voice-dropdown-item.selected {
  background: rgba(30, 118, 242, 0.05);
}

.voice-item-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.voice-item-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.voice-item-personality {
  font-size: 12px;
  color: #666;
  opacity: 0.8;
}

.voice-checkmark {
  color: #1e76f2;
  flex-shrink: 0;
}

/* Mobile adjustments */
@media (max-width: 480px) {
  .voice-display-row {
    flex-direction: column;
    align-items: stretch;
  }

  .voice-action-buttons {
    width: 100%;
  }

  .voice-play-btn,
  .voice-change-btn {
    flex: 1;
  }
}
`}</style>

              </>
            )}
          </div>
        )}
      </div>

      {/* FIXED CLEAR ALL BUTTON */}
{/* FIXED CLEAR ALL BUTTON */}
{activeTab === 'memories' && !chatMode && memories.length > 0 && (
  <div className={`fixed-clear-button-container ${isAnimatingOut ? 'is-fading-out' : ''}`}>
    <button
      onClick={() => setShowClearConfirm(true)}
      className="white-btn-danger"
    >
      <Trash2 size={14} />
      Clear All Memories
    </button>
  </div>
)}

      {/* DELETE MEMORY POPUP */}
      {showDeleteMemoryConfirm && (
        <div className="delete-popup-overlay" onClick={() => setShowDeleteMemoryConfirm(false)}>
          <div className="delete-popup" onClick={(e) => e.stopPropagation()}>
            <h3 className="delete-popup-title">Delete Memory?</h3>
            <p className="delete-popup-text">Are you sure you want to delete this memory?</p>
            <div className="delete-popup-buttons">
              <button
                onClick={() => setShowDeleteMemoryConfirm(false)}
                className="white-btn"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteMemory}
                className="white-btn-danger"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CLEAR CONFIRMATION MODAL */}
{showClearConfirm && (
  <div
    className="delete-popup-overlay"
    onClick={() => !isClearingAll && setShowClearConfirm(false)}
  >
    <div
      className="delete-popup"
      onClick={(e) => e.stopPropagation()}
    >
      {isClearingAll ? (
        // ✅ Show animated progress
        <div className="clearing-progress">
          <div className="simple-spinner" style={{ margin: '0 auto' }}></div>
          <p className="clearing-progress-text">
            Clearing {memories.length - memories.filter(m => deletingMemoryIds.has(m.id)).length} / {memories.length} memories...
          </p>
        </div>
      ) : (
        <>
          <h3 className="delete-popup-title">Clear All Memories?</h3>
          <p className="delete-popup-text">
            Are you sure you want to clear all {memories?.length || 0} memories?
            Your conversation history will be removed.
          </p>
          <div className="delete-popup-buttons">
            <button
              onClick={() => setShowClearConfirm(false)}
              className="white-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmClear}
              className="white-btn-danger"
            >
              Clear All
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)}

{/* VOICE CHANGE MODAL */}
{showVoiceChangeModal && (
  <VoiceModeModal
    isVisible={showVoiceChangeModal}
    onClose={() => setShowVoiceChangeModal(false)}
    currentVoice={selectedVoice}
    onVoiceSelected={(voice) => {
      handleVoiceUpdate(voice.configId);
    }}
    initializeVoiceMode={null} // Don't initialize, just select
  />
)}
      <style jsx>{`
        .memory-loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
        }

        .simple-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e2e8f0;
          border-top-color: #a0aec0;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .memory-loading-text {
          font-size: 14px;
          color: #718096;
          margin: 0;
        }
      `}</style>
    </>
  );
};

export default SettingsModal;